import React, { useRef, useState, useEffect } from 'react';
import ReactToPrint from 'react-to-print';
import isEmpty from 'lodash/isEmpty';

import ConnectPrintModal from './ConnectPrintModal.component';
import ThinClientPrintModal from './ThinClientPrintModal.component';
import MediaGen from './MediaGen';
import ThinClientInterface from '../thinclient/ThinClientInterface';
import Naws from '../naws';
import Utils from '../utils';
import FormsModal from './FormsModal.component';
import PdfPreviewModal from './PdfPreviewModal.component';
import SafariWarningMessageModal from './SafariWarningMessageModal.component';

import cssString from './cssString';

const { PluginInterface } = Naws;
const {
	Communication,
	PrintUtil,
	withLayoutAndModalContexts,
	ThinClient: { isInThinClient },
} = Utils;

const PrintEngine = ({
	labelData: { layoutConfig = {}, labelUrls = [], formUrls = [] } = {},
	isReprint = false,
	resetPrint = () => {},
	modalContext: { showError, showModal, closeModal },
	layoutContext: { browserName, isMobile },
}) => {
	const [labelUrlsForPrint, setLabelUrlsForPrint] = useState([]);
	const [formUrlsForPrint, setFormUrlsForPrint] = useState([]);
	const [browserPrint, setBrowserPrint] = useState(false);
	const printRef = useRef(null);
	const buttonRef = useRef(null);

	const triggerPdfPrint = imageUrls => {
		showModal({
			component: PdfPreviewModal,
			data: {
				pdfUrls: imageUrls || labelUrls, // use images from MediaGen if provided
			},
		});

		resetPrint();
	};

	const triggerBrowserPrint = async () => {
		setBrowserPrint(true);
	};

	/* for future implementation of non-media gen connect reprints
	const continueConnectPrint = async (printerName, printerTray) => {
		const reprintParams = {
			url: labelUrls[0],
			rotationDegrees: 0,
			horizontalOffset: 0,
			verticalOffset: 0,
			margin: 0,
			printerName,
			printerTray,
			printerPaper: '',
			printerPaperWidth: 4,
			printerPaperHeight: 6,
			printerPaperOrientation: 'portrait',
		};
		const reprintCommandResponse = await Communication.getReprintCommand(reprintParams);

		if (Communication.isSuccessful(reprintCommandResponse)) {
			const { reprintCommand, reprintCommandX } = reprintCommandResponse?.data;

			PluginInterface.print({ reprintCommand, reprintCommandX }, isReprint, response => {
				if (!Communication.isSuccessful(response)) {
					showError({ response });
				}
			});
		} else {
			showError({ reprintCommandResponse });
		}

		resetPrint();
	};
	*/

	// connect reprint flow using MediaGen to generate printCommand
	const continueConnectPrintWithMediaGen = async (printerName, printerTray, modalId) => {
		const config = {
			...layoutConfig,
			isThinClient: false,
			printerName,
			printerTray,
		};
		const stitchResponse = await MediaGen.stitch(labelUrls, config);

		if (Communication.isSuccessful(stitchResponse)) {
			const { printCommand, printCommandX } = stitchResponse?.data?.[0];

			PluginInterface.print({ printCommand, printCommandX }, isReprint, response => {
				if (!Communication.isSuccessful(response)) {
					showError({ response });
				}
				// close the connect modal after print
				closeModal(modalId);
			});
		} else {
			showError({ stitchResponse });
		}

		resetPrint();
	};

	// ThinClient reprint flow using MediaGen to generate printCommand
	const continueThinClientPrintWithMediaGen = async (printerName, printerTray, modalId) => {
		const config = {
			...layoutConfig,
			isThinClient: true,
			printerName,
			printerTray,
		};
		const stitchResponse = await MediaGen.stitch(labelUrls, config);

		if (Communication.isSuccessful(stitchResponse)) {
			const { printCommand } = stitchResponse?.data?.[0];

			ThinClientInterface.print(printCommand, () => {
				// close the connect modal after print
				closeModal(modalId);
			});
		} else {
			showError({ stitchResponse });
		}

		resetPrint();
	};

	const triggerConnectPrint = () => {
		showModal({
			component: ConnectPrintModal,
			data: {
				content: () => printRef.current,
				callback: continueConnectPrintWithMediaGen,
			},
		});
	};

	const triggerThinClientPrint = async () => {
		showModal({
			component: ThinClientPrintModal,
			data: {
				content: () => printRef.current,
				callback: continueThinClientPrintWithMediaGen,
			},
		});
	};

	const triggerMediaGen = async () => {
		const { labelFormat } = layoutConfig;
		const response = await MediaGen.stitch(labelUrls, layoutConfig);

		if (Communication.isSuccessful(response)) {
			const { imageUrl } = response?.data?.[0];

			if (isInThinClient()) {
				triggerThinClientPrint();
			} else if (labelFormat === 'png') {
				setLabelUrlsForPrint([imageUrl]);
				triggerBrowserPrint();
			} else if (labelFormat === 'pdf') {
				triggerPdfPrint([imageUrl]);
			}
		} else {
			showError({ response });
		}
	};

	// split the print flow into 4 separate functions based on the print type
	const continuePrint = modalId => {
		const { labelFormat, printLayout } = layoutConfig;

		if (modalId) {
			closeModal(modalId);
		}

		if (PrintUtil.isPluginPrintFlow(layoutConfig)) {
			triggerConnectPrint();
		} else if (MediaGen.canStitch(printLayout, labelUrls, labelFormat)) {
			triggerMediaGen();
		} else if (labelFormat === 'pdf') {
			triggerPdfPrint();
		} else if (isInThinClient()) {
			triggerThinClientPrint();
		} else if (labelFormat === 'png') {
			triggerBrowserPrint();
		}
	};

	const startPrint = () => {
		setFormUrlsForPrint(formUrls);

		PluginInterface.getSessions(() => {
			if (PrintUtil.isPluginPrintFlow(layoutConfig)) {
				// continue the print with SDC Connect
				triggerConnectPrint();
			} else if (
				browserName === 'Safari' &&
				localStorage.getItem('dismissSafariPrintWarning') !== 'true' &&
				!isMobile
			) {
				showModal({
					component: SafariWarningMessageModal,
					data: { callback: continuePrint },
				});
			} else {
				continuePrint();
			}
		});
	};

	const onAfterPrint = () => {
		resetPrint();

		if (!isEmpty(formUrlsForPrint)) {
			showModal({
				component: FormsModal,
				data: {
					isReprint,
					formUrls: formUrlsForPrint,
				},
			});
		}
	};

	const renderBrowserPrint = () => {
		const { printOn } = layoutConfig;
		const classes = `${Utils.Util.getOS(true)
			.toLowerCase()
			.replaceAll(' ', '')} ${Utils.Util.getBrowser(true).toLowerCase()} ${printOn}`;

		return (
			<>
				<ReactToPrint
					trigger={() => (
						<button type="button" style={{ display: 'none' }} ref={buttonRef}>
							Print
						</button>
					)}
					content={() => printRef.current}
					onAfterPrint={onAfterPrint}
					pageStyle={cssString}
					bodyClass={classes}
				/>
				<div style={{ display: 'none' }}>
					<div ref={printRef}>
						{labelUrlsForPrint.map((url, i) => {
							let labelUrl = url;
							// Add 'r90' to rotate it for printing
							if (
								printOn.indexOf('shippingLabel') > -1 ||
								printOn.indexOf('envelopeCM') > -1
							) {
								labelUrl = labelUrl.replace('200-h0', '200-r90-h0');
							} else if (
								printOn.indexOf('envelope') > -1 &&
								printOn.indexOf('envelopeCM') === -1
							) {
								labelUrl = labelUrl.replace('200', '200-r90');
							}
							return (
								<div key={`print-image-${i}`} className="image-container">
									<img
										alt="Label to be printed."
										className={`${classes}`}
										src={labelUrl}
									/>
								</div>
							);
						})}
					</div>
				</div>
			</>
		);
	};

	useEffect(() => {
		setLabelUrlsForPrint(labelUrls);
		startPrint();
	}, []);

	useEffect(() => {
		if (labelUrls.length > 0 && buttonRef.current && browserPrint) {
			buttonRef.current.click();
			setBrowserPrint(false);
		}
	}, [browserPrint]);

	return <>{browserPrint && renderBrowserPrint()}</>;
};

export default withLayoutAndModalContexts(PrintEngine);
